import { useEffect, useState, useRef } from "react";
import {
  LifeBuoy,
  RefreshCw,
  Send,
  CheckCircle2,
  Clock,
  MessageSquare,
  Smartphone,
  Globe,
  User,
  ExternalLink,
  ChevronRight,
} from "lucide-react";
import { api } from "../api/staff";

interface Contact {
  id: number;
  name: string;
  identifier: string;
  publicId?: string | null;
  telegramId?: number | null;
  username?: string | null;
}

interface TicketSummary {
  id: number;
  status: "open" | "resolved";
  channel: "telegram" | "web";
  createdAt: number;
  lastActivityAt: number;
  contact: Contact;
  unreadCount: number;
  lastMessage?: {
    id: number;
    content: string;
    senderType: "user" | "staff";
    createdAt: number;
  } | null;
}

interface MessageItem {
  id: number;
  content: string;
  senderType: "user" | "staff";
  senderName: string;
  createdAt: number;
}

interface TicketDetail {
  id: number;
  status: "open" | "resolved";
  channel: "telegram" | "web";
  createdAt: number;
  contact: Contact;
  messages: MessageItem[];
}

export function SupportDashboard() {
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | "open" | "resolved">("open");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const data = await api<{ tickets: TicketSummary[] }>(`/staff/support/tickets?status=${filterStatus}`);
      setTickets(data.tickets || []);
      if (data.tickets && data.tickets.length > 0 && !selectedTicketId && data.tickets[0]) {
        setSelectedTicketId(data.tickets[0].id);
      }
    } catch (err) {
      console.error("Failed to load tickets", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const data = await api<TicketDetail>(`/staff/support/tickets/${id}`);
      setSelectedTicket(data);
    } catch (err) {
      console.error("Failed to load ticket detail", err);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 10000); // 10s auto-refresh
    return () => clearInterval(interval);
  }, [filterStatus]);

  useEffect(() => {
    if (selectedTicketId) {
      fetchTicketDetail(selectedTicketId);
    } else {
      setSelectedTicket(null);
    }
  }, [selectedTicketId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedTicket?.messages]);

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicketId || !replyText.trim() || sending) return;

    setSending(true);
    try {
      await api(`/staff/support/tickets/${selectedTicketId}/reply`, {
        method: "POST",
        body: JSON.stringify({ content: replyText.trim() }),
      });
      setReplyText("");
      await fetchTicketDetail(selectedTicketId);
      fetchTickets();
    } catch (err) {
      console.error("Failed to send reply", err);
    } finally {
      setSending(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedTicket) return;
    const nextStatus = selectedTicket.status === "open" ? "resolved" : "open";
    try {
      await api(`/staff/support/tickets/${selectedTicket.id}/status`, {
        method: "POST",
        body: JSON.stringify({ status: nextStatus }),
      });
      setSelectedTicket((prev) => prev ? { ...prev, status: nextStatus } : null);
      fetchTickets();
    } catch (err) {
      console.error("Failed to update status", err);
    }
  };

  const formatTimestamp = (ts: number) => {
    if (!ts) return "";
    const date = new Date(ts * 1000);
    return date.toLocaleString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
    });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-6.5rem)] space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
            <LifeBuoy className="w-6 h-6 text-primary" />
            Support Client & Tickets
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Interface modérateur native — Conversations en direct Web & Telegram
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Status Tabs */}
          <div className="flex rounded-lg bg-muted p-1 text-xs font-medium">
            <button
              onClick={() => setFilterStatus("open")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                filterStatus === "open" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Ouverts
            </button>
            <button
              onClick={() => setFilterStatus("resolved")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                filterStatus === "resolved" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Résolus
            </button>
            <button
              onClick={() => setFilterStatus("all")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                filterStatus === "all" ? "bg-background text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Tous
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              fetchTickets();
              if (selectedTicketId) fetchTicketDetail(selectedTicketId);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Main Grid */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4 min-h-0">
        {/* Ticket List (Left column) */}
        <div className="md:col-span-4 lg:col-span-4 rounded-xl border border-border bg-card flex flex-col overflow-hidden shadow-xs">
          <div className="p-3 border-b border-border bg-muted/30 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
              Conversations ({tickets.length})
            </span>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {tickets.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                Aucun ticket {filterStatus === "open" ? "ouvert" : ""}
              </div>
            ) : (
              tickets.map((t) => {
                const isSelected = t.id === selectedTicketId;
                return (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTicketId(t.id)}
                    className={`w-full text-left p-3.5 transition-colors flex items-start gap-3 ${
                      isSelected ? "bg-accent/70 border-l-4 border-l-primary" : "hover:bg-muted/40"
                    }`}
                  >
                    <div className="mt-0.5 shrink-0">
                      {t.channel === "telegram" ? (
                        <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 flex items-center justify-center">
                          <Smartphone className="w-4 h-4" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/20 text-primary flex items-center justify-center">
                          <Globe className="w-4 h-4" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-semibold text-sm truncate text-foreground">
                          {t.contact.name}
                        </span>
                        <span className="text-[11px] text-muted-foreground shrink-0">
                          {formatTimestamp(t.lastActivityAt || t.createdAt)}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${
                          t.status === "open"
                            ? "bg-amber-500/15 text-amber-400 border border-amber-500/20"
                            : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                        }`}>
                          {t.status === "open" ? "Ouvert" : "Résolu"}
                        </span>
                        {t.contact.username && (
                          <span className="text-[11px] text-muted-foreground truncate">
                            @{t.contact.username}
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1 truncate">
                        {t.lastMessage ? t.lastMessage.content : "Nouvelle conversation"}
                      </p>
                    </div>

                    <ChevronRight className={`w-4 h-4 self-center text-muted-foreground/40 ${isSelected ? "text-primary" : ""}`} />
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Thread (Right column) */}
        <div className="md:col-span-8 lg:col-span-8 rounded-xl border border-border bg-card flex flex-col overflow-hidden shadow-xs">
          {selectedTicket ? (
            <>
              {/* Ticket Top bar */}
              <div className="p-4 border-b border-border bg-muted/20 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-foreground font-bold">
                    <User className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-bold text-foreground">
                        {selectedTicket.contact.name}
                      </h2>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedTicket.status === "open"
                          ? "bg-amber-500/15 text-amber-400 border border-amber-500/20"
                          : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
                      }`}>
                        {selectedTicket.status === "open" ? "En cours" : "Résolu"}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                      <span>Canal : {selectedTicket.channel === "telegram" ? "Telegram Bot" : "Web Client"}</span>
                      {selectedTicket.contact.telegramId && (
                        <span>ID TG : {selectedTicket.contact.telegramId}</span>
                      )}
                      {selectedTicket.contact.publicId && (
                        <span>Public ID : {selectedTicket.contact.publicId}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleToggleStatus}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      selectedTicket.status === "open"
                        ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 border border-emerald-500/30"
                        : "bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 border border-amber-500/30"
                    }`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {selectedTicket.status === "open" ? "Marquer comme résolu" : "Rouvrir le ticket"}
                  </button>
                </div>
              </div>

              {/* Messages list */}
              <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-muted/5">
                {detailLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : selectedTicket.messages.length === 0 ? (
                  <div className="p-8 text-center text-sm text-muted-foreground">
                    Aucun message dans cette conversation.
                  </div>
                ) : (
                  selectedTicket.messages.map((m) => {
                    const isStaff = m.senderType === "staff";
                    return (
                      <div
                        key={m.id}
                        className={`flex flex-col ${isStaff ? "items-end" : "items-start"}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1 px-1">
                          <span className="text-[11px] font-medium text-muted-foreground">
                            {isStaff ? "Modérateur (Vous)" : selectedTicket.contact.name}
                          </span>
                          <span className="text-[10px] text-muted-foreground/60">
                            {formatTimestamp(m.createdAt)}
                          </span>
                        </div>
                        <div
                          className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                            isStaff
                              ? "bg-primary text-primary-foreground rounded-br-xs"
                              : "bg-muted text-foreground border border-border rounded-bl-xs"
                          }`}
                        >
                          {m.content}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply box */}
              <form
                onSubmit={handleSendReply}
                className="p-3 border-t border-border bg-card flex items-end gap-2"
              >
                <textarea
                  rows={2}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply(e);
                    }
                  }}
                  placeholder="Écrivez votre réponse au joueur... (Entrée pour envoyer)"
                  className="flex-1 min-h-[44px] max-h-32 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
                <button
                  type="submit"
                  disabled={!replyText.trim() || sending}
                  className="h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium text-xs flex items-center gap-1.5 hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0"
                >
                  <Send className="w-3.5 h-3.5" />
                  {sending ? "Envoi..." : "Envoyer"}
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
              <LifeBuoy className="w-12 h-12 mb-3 opacity-20" />
              <p className="text-base font-semibold text-foreground">Sélectionnez une conversation</p>
              <p className="text-sm mt-1">
                Choisissez un ticket à gauche pour afficher le fil et répondre au joueur.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
